/**
 * Gallery — the property photo carousel, plus a full-screen lightbox.
 *
 * `Gallery` shows one photo with prev/next arrows, dot indicators and a counter;
 * clicking the photo opens `GalleryLightbox`, a full-screen viewer that shares
 * the gallery's current index (so it opens on the photo you clicked) and pages
 * with the ← / → arrow keys.
 *
 * Props (Gallery):
 *   - images     {string[]} — photo URLs
 *   - photoCount {number}   — total photo count for the "1/N" badge (API total)
 *   - overlay    {ReactNode}— extra content drawn over the image (mobile Like/Reject FABs)
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Modal } from 'react-bootstrap';
import { useStrings } from './i18n';
import { IconX, IconChevronLeft, IconChevronRight } from './icons';
import { cdnSize, cdnVariants } from './mapProperty';

// Image preloading window. A property with 50+ photos would otherwise put every
// photo of every offer in the DOM at load; instead only slides near the current
// one carry a `src`. PRELOAD_RADIUS either side (~5 images) while browsing the
// page, widened forward to PRELOAD_AHEAD_OPEN once the lightbox is open.
const PRELOAD_RADIUS = 2;
const PRELOAD_AHEAD_OPEN = 10;

// Kill-switch for the swipe-release animation. `true` drives the release with
// requestAnimationFrame (translate3d written per frame) — smooth on old iOS
// WebKit, which stalls when a CSS transition is enabled in the same frame as the
// transform change. `false` falls back to the CSS-transition release. Only the
// touch-swipe release is affected; arrows/dots always use the CSS transition.
const USE_RAF_FLING = true;

// Offer the photo's size variants as a srcset so the browser picks by viewport
// AND device pixel ratio, rather than us hard-picking one width for everyone.
// The URL carries the CDN's size name (w400/w800/w1200, or "thumbnails" on v3);
// the trailing "<n>w" is the srcset width descriptor, not part of the URL.
const srcSetFor = (src) => cdnVariants(src)
    .map(({ size, width }) => `${cdnSize(src, size)} ${width}w`)
    .join(', ');
// The carousel spans the card, which is full-bleed until the 1500px max-width
// container caps it.
const GALLERY_SIZES = '(min-width: 1500px) 1450px, 100vw';

export default function Gallery({ images, photoCount, overlay }) {
    const t = useStrings();
    const n = images.length;
    // The carousel loops: the strip is cloned as [last, ...images, first] and the
    // track position (`trackPos`) indexes that cloned strip. Paging onto a clone
    // animates, then we jump (no transition) to its real twin — an endless loop.
    const offset = n > 1 ? 1 : 0;
    const [trackPos, setTrackPos] = useState(offset);
    const realIdx = n > 1 ? ((trackPos - 1) % n + n) % n : 0;
    const [open, setOpen] = useState(false); // lightbox open?
    // While the user pages photos, the compact overlay's price/spec pills collapse
    // so the image is unobstructed; they return after 5s of no navigation.
    const [navigating, setNavigating] = useState(false);
    const navTimer = useRef();
    const snapRaf = useRef();
    const releaseRaf = useRef();
    const trackRef = useRef(null);
    // Single-photo gallery: no arrows/dots/counter (there is nothing to page),
    // and a swipe attempt answers with a small rubber-band nudge to show there
    // are no other images.
    const single = n <= 1;
    const [nudge, setNudge] = useState(false);
    const nudgeTimer = useRef();
    const doNudge = () => {
        setNudge(false);
        requestAnimationFrame(() => {
            setNudge(true);
            clearTimeout(nudgeTimer.current);
            nudgeTimer.current = setTimeout(() => setNudge(false), 500);
        });
    };
    const flagNav = () => {
        setNavigating(true);
        clearTimeout(navTimer.current);
        navTimer.current = setTimeout(() => setNavigating(false), 5000);
    };
    useEffect(() => () => {
        clearTimeout(navTimer.current);
        clearTimeout(nudgeTimer.current);
        cancelAnimationFrame(snapRaf.current);
        cancelAnimationFrame(releaseRaf.current);
    }, []);

    // Ken Burns only runs while the carousel is on screen: observe the gallery and
    // toggle `is-inview`, which the CSS uses to gate (and restart) the animation.
    // The same observer flips `seen`, which gates image loading (below).
    // No IntersectionObserver → assume in view (animation just runs).
    const rootRef = useRef(null);
    const [inView, setInView] = useState(false);
    // Sticky "has been near the viewport": off-screen properties cost no image
    // requests, and scrolling back doesn't re-fetch what's already loaded.
    const [seen, setSeen] = useState(false);
    // Seed `seen` before paint for a carousel that is already on/near screen, so
    // the first property's photo (the LCP element) starts downloading immediately
    // instead of waiting a frame for the observer callback below.
    useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el || typeof window === 'undefined') return;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 1.5 && r.bottom > 0) setSeen(true);
    }, []);
    useEffect(() => {
        const el = rootRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); setSeen(true); return undefined; }
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => {
                setInView(e.isIntersecting);
                if (e.isIntersecting) setSeen(true);
                // NB: scrolling a property into view deliberately does NOT collapse
                // the compact glass. Price/pills are shown by default and only get
                // out of the way while paging photos (flagNav) or once the card is
                // expanded (the glass inset belongs to the collapsed state).
            }),
            { threshold: 0.25 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);
    // The cloned strip: [lastImage, ...images, firstImage] (single image → no clones).
    const ext = n > 1 ? [images[n - 1], ...images, images[0]] : images;

    const [dx, setDx] = useState(0);   // live drag offset (px) applied to the track
    const [anim, setAnim] = useState(true); // transition on? off while dragging / during the loop snap

    // Page by d slides (arrows / swipe). Landing on a clone is fixed up in
    // onTrackTransitionEnd, so this normally just runs past an end and loops.
    //
    // Guard for fast navigation (mashing the arrow keys): if we're already sitting
    // on a clone edge because the loop-snap transition hasn't fired yet, stepping
    // again would push trackPos past the cloned strip [0, n+1] and scroll into
    // blank space. So first jump instantly (no transition) to the clone's real
    // twin — the same image, so nothing visibly moves — then animate the step on
    // the next frame. This keeps trackPos in range no matter how fast we page.
    const step = (d) => {
        if (n <= 1) return;
        // Kill any in-flight swipe fling: its settle (setTrackPos) fires ~260ms
        // after release, so a step() during that window (e.g. opening the lightbox
        // and tapping next right after a swipe) would advance, then get clobbered
        // back to the swipe's target — a bounce. Cancelling here stops that.
        cancelAnimationFrame(releaseRaf.current);
        flagNav();
        if (trackPos === 0 || trackPos === n + 1) {
            const twin = trackPos === 0 ? n : 1;
            setAnim(false);
            setTrackPos(twin);
            cancelAnimationFrame(snapRaf.current);
            snapRaf.current = requestAnimationFrame(() => {
                setAnim(true);
                setTrackPos(twin + d);
            });
            return;
        }
        setAnim(true);
        setTrackPos((p) => p + d);
    };
    // Jump straight to a real index (lightbox thumbnails).
    const goTo = (i) => { cancelAnimationFrame(releaseRaf.current); setAnim(true); setTrackPos(i + offset); flagNav(); };
    // After a slide settles on a clone, snap (no transition) to its real twin.
    const onTrackTransitionEnd = () => {
        if (n <= 1) return;
        if (trackPos === 0) { setAnim(false); setTrackPos(n); }
        else if (trackPos === n + 1) { setAnim(false); setTrackPos(1); }
    };

    // Touch drag (mobile): the whole strip follows the finger in real time, then
    // snaps to the nearest photo on release (looping over the cloned ends). `vx`
    // tracks the recent finger velocity (px/ms) so a quick flick advances even if
    // it didn't travel far, and the release keeps the flick's momentum.
    const drag = useRef({ x: 0, y: 0, w: 1, active: false, horiz: false, moved: false, dx: 0, vx: 0, lastDx: 0, lastT: 0 });

    const onTouchStart = (e) => {
        cancelAnimationFrame(releaseRaf.current);
        const p = e.touches[0];
        drag.current = {
            x: p.clientX, y: p.clientY, w: e.currentTarget.offsetWidth || 1,
            active: true, horiz: false, moved: false, dx: 0, vx: 0, lastDx: 0, lastT: Date.now(),
        };
    };
    const onTouchMove = (e) => {
        const d = drag.current;
        if (!d.active) return;
        if (n <= 1) {
            // Single photo: a clear horizontal swipe gets the rubber-band nudge.
            const p1 = e.touches[0];
            const mx = p1.clientX - d.x;
            if (Math.abs(mx) > 24 && Math.abs(mx) > Math.abs(p1.clientY - d.y)) {
                d.active = false;
                doNudge();
            }
            return;
        }
        const p = e.touches[0];
        const ddx = p.clientX - d.x;
        const ddy = p.clientY - d.y;
        // lock the axis once out of a small deadzone; bail on vertical (page scroll)
        if (!d.horiz) {
            if (Math.abs(ddx) < 6 && Math.abs(ddy) < 6) return;
            d.horiz = Math.abs(ddx) > Math.abs(ddy);
            if (!d.horiz) { d.active = false; return; }
            flagNav();
        }
        d.moved = true;
        // Instantaneous velocity since the last move (px/ms), for flick detection.
        const now = Date.now();
        const dt = now - d.lastT;
        if (dt > 0) d.vx = (ddx - d.lastDx) / dt;
        d.lastDx = ddx;
        d.lastT = now;
        d.dx = ddx;
        setDx(ddx);
        setAnim(false);
    };
    // rAF-driven release: instead of handing off to a CSS transition (which
    // stalls on old iOS WebKit — the transform change after enabling the
    // transition doesn't paint, so the track freezes then snaps), we animate the
    // track ourselves, writing translate3d directly to the node each frame. This
    // is the SAME direct-transform path that makes the finger drag smooth, so the
    // release just continues it. On completion we commit the logical position with
    // no transition (batched, so React 16 doesn't paint an intermediate frame at
    // the wrong offset), snapping a clone edge to its real twin.
    const flingTo = (targetPos, fromPx, w, duration) => {
        const endPx = -targetPos * w;
        const startPx = fromPx;
        const start = Date.now();
        const easeOut = (p) => 1 - Math.pow(1 - p, 3); // easeOutCubic: fast start, decelerate
        cancelAnimationFrame(releaseRaf.current);
        const tick = () => {
            const p = Math.min(1, (Date.now() - start) / duration);
            const px = startPx + (endPx - startPx) * easeOut(p);
            const node = trackRef.current;
            if (node) node.style.transform = `translate3d(${px}px, 0, 0)`;
            if (p < 1) { releaseRaf.current = requestAnimationFrame(tick); return; }
            let finalPos = targetPos;
            if (n > 1 && targetPos === 0) finalPos = n;
            else if (n > 1 && targetPos === n + 1) finalPos = 1;
            ReactDOM.unstable_batchedUpdates(() => {
                setAnim(false);
                setDx(0);
                setTrackPos(finalPos);
            });
            // Re-enable the CSS transition a frame later (with nothing else
            // changing, so it doesn't animate) so later arrow/dot nav still eases.
            releaseRaf.current = requestAnimationFrame(() => setAnim(true));
        };
        releaseRaf.current = requestAnimationFrame(tick);
    };

    const onTouchEnd = () => {
        const d = drag.current;
        if (!d.active) return;
        d.active = false;
        // Only a real horizontal drag flings. A tap (e.g. on the prev/next arrow,
        // whose touch bubbles up here) or a vertical scroll must NOT run flingTo:
        // its delayed settle would commit trackPos back to where the gesture
        // started, overwriting the arrow's step() and bouncing to the prev photo.
        if (n <= 1 || !d.horiz || !d.moved) { if (dx !== 0) setDx(0); return; }
        // Advance if the drag passed the distance threshold OR was a quick flick
        // (velocity), so a fast short swipe still pages. Direction follows the flick
        // velocity when it's a flick, otherwise the drag distance.
        const w = d.w || 1;
        const threshold = Math.min(60, w * 0.2);
        const FLICK = 0.35; // px/ms ≈ 350 px/s
        const flick = Math.abs(d.vx) > FLICK;
        const advance = flick || Math.abs(d.dx) > threshold;
        const dir = advance ? ((flick ? d.vx : d.dx) < 0 ? 1 : -1) : 0;

        if (!USE_RAF_FLING) {
            // Fallback: two-frame CSS-transition release (enable the transition
            // this frame with the track still at the dragged offset, move to the
            // target next frame — so WebKit registers a start state to ease from).
            setAnim(true);
            cancelAnimationFrame(releaseRaf.current);
            releaseRaf.current = requestAnimationFrame(() => {
                setDx(0);
                if (dir) step(dir);
            });
            return;
        }

        const targetPos = trackPos + dir;
        const fromPx = -trackPos * w + d.dx;
        const remaining = Math.abs(-targetPos * w - fromPx);
        // Momentum: on a flick, cover the remaining distance at roughly the
        // finger's speed (clamped); otherwise a fixed snappy settle.
        let duration = 260;
        if (flick) {
            const v = Math.max(Math.abs(d.vx), 0.1);
            duration = Math.min(340, Math.max(140, remaining / v));
        }
        flagNav();
        flingTo(targetPos, fromPx, w, duration);
    };
    // suppress the lightbox tap when the gesture was a drag
    const onImgClick = () => { if (!drag.current.moved) setOpen(true); };

    const trackStyle = {
        transform: `translate3d(calc(${-trackPos * 100}% + ${dx}px), 0, 0)`,
        // ease-OUT (fast start, decelerate) so the release continues the finger's
        // momentum instead of easing up from a standstill — `ease` slow-started,
        // which read as the image stopping then lurching to the next one.
        transition: anim ? 'transform .28s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
    };

    // Instagram-style dots: at most 5 shown as a window that slides with the
    // current photo; when there are more, the dots nearest the hidden overflow
    // shrink ('sm' then 'xs') to hint there's more on that side.
    const WINDOW = 5;
    const dotset = (() => {
        if (n <= WINDOW) return images.map((_, i) => ({ i, size: 'n' }));
        const start = Math.min(Math.max(realIdx - 2, 0), n - WINDOW);
        const moreLeft = start > 0;
        const moreRight = start + WINDOW < n;
        return Array.from({ length: WINDOW }, (_, k) => {
            const i = start + k;
            let size = 'n';
            if (moreLeft && k === 0) size = 'xs';
            else if (moreLeft && k === 1) size = 'sm';
            if (moreRight && k === WINDOW - 1) size = 'xs';
            else if (moreRight && k === WINDOW - 2) size = 'sm';
            if (i === realIdx) size = 'n'; // the active dot is never shrunk
            return { i, size };
        });
    })();
    // Slides near the current one carry a real `src`; the rest render as an empty
    // (CSS-tinted) box until paged to. On load that's ~5 images (current ±2);
    // opening the lightbox widens it forward so the next 10 are ready to page
    // through. Distance is measured around the looping strip.
    const ahead = open ? PRELOAD_AHEAD_OPEN : PRELOAD_RADIUS;
    const inWindow = (i) => {
        if (!seen) return false;
        let d = i - trackPos;
        if (d > ext.length / 2) d -= ext.length;
        if (d < -ext.length / 2) d += ext.length;
        return d >= -PRELOAD_RADIUS && d <= ahead;
    };
    // Which real photo a (possibly cloned) strip index maps to — clones share a src.
    const realIndexOf = (i) => (n > 1 ? ((i - 1) % n + n) % n : 0);

    // Once a photo has loaded, keep its src. Dropping it when the window moved made
    // the outgoing slide flash white and forced a re-decode mid-swipe, which lagged
    // badly on old iOS Chrome. This only grows within a gallery you've browsed;
    // off-screen galleries still load nothing.
    const [everLoaded, setEverLoaded] = useState(() => new Set());
    useEffect(() => {
        setEverLoaded((prev) => {
            let next = prev;
            for (let i = 0; i < ext.length; i += 1) {
                if (inWindow(i)) {
                    const r = realIndexOf(i);
                    if (!next.has(r)) { if (next === prev) next = new Set(prev); next.add(r); }
                }
            }
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seen, trackPos, open, n]);

    // Render a src if it's in the live window (immediate, for a fast first paint)
    // or has ever loaded (sticky, so it never blanks mid-swipe).
    const shouldLoad = (i) => inWindow(i) || everLoaded.has(realIndexOf(i));

    return (
        <div ref={rootRef} className={`rs-gallery ${navigating ? 'is-nav' : ''} ${inView ? 'is-inview' : ''}`}
            onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
            <div ref={trackRef} className={`rs-gallery__track${nudge ? ' is-nudge' : ''}`} style={trackStyle} onTransitionEnd={onTrackTransitionEnd}>
                {ext.map((src, i) => {
                    // The element stays mounted before it loads so its aspect-ratio
                    // keeps sizing the slide. Until it has an image it is a plain
                    // placeholder: empty alt and NO role/aria — an empty alt already
                    // maps to the presentation role, and pairing that with
                    // role="button" + aria-label is an a11y conflict.
                    const loaded = shouldLoad(i);
                    return (
                        <div key={i} className={`rs-gallery__slide ${i === trackPos ? 'is-active' : ''}`}>
                            <img className="rs-gallery__img" src={loaded ? src : undefined}
                                srcSet={loaded ? srcSetFor(src) : undefined}
                                sizes={loaded ? GALLERY_SIZES : undefined}
                                alt={loaded ? `${t.photo} ${i + 1}` : ''}
                                loading={i === trackPos ? 'eager' : 'lazy'} decoding="async"
                                draggable={false}
                                {...(loaded ? { role: 'button', 'aria-label': t.openGallery } : {})}
                                onClick={onImgClick} />
                        </div>
                    );
                })}
            </div>
            {/* Single-photo galleries drop the paging chrome entirely. */}
            {!single && (
                <>
                    <button className="rs-gallery__nav rs-gallery__nav--prev" onClick={() => step(-1)} aria-label={t.prevPhoto}>
                        <IconChevronLeft />
                    </button>
                    <button className="rs-gallery__nav rs-gallery__nav--next" onClick={() => step(1)} aria-label={t.nextPhoto}>
                        <IconChevronRight />
                    </button>
                    <div className="rs-gallery__dots">
                        {dotset.map(({ i, size }) => (
                            <span key={i}
                                className={`rs-gallery__dot rs-gallery__dot--${size} ${i === realIdx ? 'is-active' : ''}`} />
                        ))}
                    </div>
                    <div className="rs-gallery__count">{String(realIdx + 1).padStart(2, '0')} / {photoCount}</div>
                </>
            )}
            {overlay}

            <GalleryLightbox
                show={open}
                onHide={() => setOpen(false)}
                images={images}
                idx={realIdx}
                onGo={step}
                onSelect={goTo}
            />
        </div>
    );
}

/* Full-screen photo viewer: large image on a near-opaque backdrop with side nav,
   a counter and a thumbnail strip. Index is owned by the parent Gallery so the
   inline carousel and the lightbox stay in sync. Arrow keys page while open. */
function GalleryLightbox({ show, onHide, images, idx, onGo, onSelect }) {
    const t = useStrings();
    useEffect(() => {
        if (!show) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowRight') onGo(1);
            else if (e.key === 'ArrowLeft') onGo(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [show, onGo]);

    // Click the background to close. The image element fills the stage
    // (width/height 100% so small photos still scale up), so its letterbox counts
    // as "background" — clicking the image closes too (below). The container
    // handlers cover any remaining empty areas, gated on target === currentTarget
    // so clicks on a child (nav / thumbs / close) never bubble into a close.
    const closeOnBg = (e) => { if (e.target === e.currentTarget) onHide(); };
    // The modal is a React portal, so its touch events still bubble through the
    // React tree to Gallery's swipe handlers. Keep thumbnail drags inside the
    // horizontally scrollable strip without preventing the browser's scrolling.
    const stopGallerySwipe = (e) => e.stopPropagation();

    const thumbListRef = useRef(null);
    const isFirstImageChangeAfterOpen = useRef(true);

    const scrollToActiveThumbnail = (idx, imagesCount, useInstantBehavior) => {
        const thumbList = thumbListRef.current;

        if (thumbList) {
            const activeThumbItem = thumbList.querySelector('.rs-lightbox__thumb.is-active');

            if (activeThumbItem) {
                const thumbListRect = thumbList.getBoundingClientRect();
                const thumbItemRect = activeThumbItem.getBoundingClientRect();

                const relativeLeft = thumbItemRect.left - thumbListRect.left;
                const relativeRight = thumbItemRect.right - thumbListRect.right;
                const isThumbItemFullyVisible = relativeLeft >= 0 && relativeRight <= 0;

                if (!isThumbItemFullyVisible) {
                    const isFirstOrLastItem = idx === 0 || idx === imagesCount - 1;
                    const isDirectionLeft = relativeLeft < 0;

                    activeThumbItem.scrollIntoView({
                        behavior: useInstantBehavior || isFirstOrLastItem ? 'instant' : 'smooth',
                        inline: isDirectionLeft ? 'end' : 'start'
                    });
                }
            }
        }
    };

    useEffect(() => {
        if (!show) {
            isFirstImageChangeAfterOpen.current = true;
            return;
        }

        scrollToActiveThumbnail(idx, images.length, isFirstImageChangeAfterOpen.current);
        isFirstImageChangeAfterOpen.current = false;
    }, [show, idx, images]);

    return (
        <Modal show={show} onHide={onHide}
            dialogClassName="rs-lightbox" backdropClassName="rs-lightbox-backdrop">
            <Modal.Body>
                <div className="rs-proto" onClick={closeOnBg}>
                    <div className="rs-lightbox__inner" onClick={closeOnBg}>
                        <button type="button" className="rs-lightbox__close" aria-label={t.close} onClick={onHide}>
                            <IconX />
                        </button>
                        <div className="rs-lightbox__stage" onClick={closeOnBg}>
                            {images.length > 1 && (
                                <button type="button" className="rs-lightbox__nav rs-lightbox__nav--prev"
                                    onClick={() => onGo(-1)} aria-label={t.prevPhoto}><IconChevronLeft /></button>
                            )}
                            <img className="rs-lightbox__img" src={images[idx]}
                                srcSet={srcSetFor(images[idx])} sizes="100vw"
                                alt={`${t.photo} ${idx + 1}`}
                                onClick={onHide} />
                            {images.length > 1 && (
                                <button type="button" className="rs-lightbox__nav rs-lightbox__nav--next"
                                    onClick={() => onGo(1)} aria-label={t.nextPhoto}><IconChevronRight /></button>
                            )}
                            <div className="rs-lightbox__count">{idx + 1}/{images.length}</div>
                        </div>
                        {images.length > 1 && (
                            <div ref={thumbListRef} className="rs-lightbox__thumbs"
                                onTouchStart={stopGallerySwipe} onTouchMove={stopGallerySwipe}
                                onTouchEnd={stopGallerySwipe} onTouchCancel={stopGallerySwipe}>
                                {images.map((src, i) => (
                                    <button type="button" key={i}
                                        className={`rs-lightbox__thumb ${i === idx ? 'is-active' : ''}`}
                                        onClick={() => onSelect(i)} aria-label={`${t.photo} ${i + 1}`}>
                                        {/* thumb strip: small CDN variant, not the full-size photo */}
                                        <img src={cdnSize(src, 'thumbnails')} alt="" loading="lazy" decoding="async" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}
